import type { CommentAnchor } from '../../services/comments';

type Box = { x: number; y: number; width: number; height: number };
type Point = { x: number; y: number };

const inside = (box: Box, at: Point) => at.x >= box.x && at.x <= box.x + box.width && at.y >= box.y && at.y <= box.y + box.height;

/**
 * Where a press of the Comment tool pins a thread (W5-AR2): on the topmost layer under it, as an
 * offset from that layer's top-left, so the pin moves with the layer; on the bare page, the point.
 * The artboard under it, and where on it, are kept too: the view page shows pins by artboard.
 */
export function anchorAt(o: {
  pageId: string;
  world: Point;
  /** Layers under the press, topmost first, artboards included. */
  layers: readonly { id: string; artboard: boolean }[];
  boxOf(id: string): Box | null;
  /** The page's artboards, in any order. */
  boards: readonly string[];
}): CommentAnchor {
  const layer = o.layers.find((one) => !one.artboard);
  const box = layer ? o.boxOf(layer.id) : null;
  const board = o.boards.map((id) => ({ id, box: o.boxOf(id) })).find((one) => one.box && inside(one.box, o.world));
  return {
    pageId: o.pageId,
    nodeId: layer && box ? layer.id : null,
    x: layer && box ? o.world.x - box.x : o.world.x,
    y: layer && box ? o.world.y - box.y : o.world.y,
    boardId: board?.id ?? null,
    boardX: board?.box ? o.world.x - board.box.x : null,
    boardY: board?.box ? o.world.y - board.box.y : null,
  };
}

/** Where a pin is on the page now: on its layer as the layer stands, or where it was put. */
export function pinPoint(anchor: CommentAnchor, boxOf: (id: string) => Box | null): Point {
  const box = anchor.nodeId ? boxOf(anchor.nodeId) : null;
  return box ? { x: box.x + anchor.x, y: box.y + anchor.y } : anchor.nodeId && anchor.boardId ? boardPoint(anchor, boxOf) : { x: anchor.x, y: anchor.y };
}

/** A pin whose layer is gone stays where it fell on its artboard, which is still there. */
function boardPoint(anchor: CommentAnchor, boxOf: (id: string) => Box | null): Point {
  const board = anchor.boardId ? boxOf(anchor.boardId) : null;
  return board && anchor.boardX != null && anchor.boardY != null ? { x: board.x + anchor.boardX, y: board.y + anchor.boardY } : { x: anchor.x, y: anchor.y };
}

/** "10 min", "1 h", "8 Oct": how long ago, the way the thread list says it. */
export function ago(iso: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const at = new Date(iso);
  return `${at.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][at.getMonth()]}`;
}

/** The @ being typed at the caret, if any: what the mention list filters by. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@([\p{L}\p{N}._-]{0,30})$/u.exec(before);
  return match ? { start: caret - match[2]!.length - 1, query: match[2]! } : null;
}
